/**
 * Ontology Graph Explorer - Main Container Component
 *
 * Interactive graph viewer for OntologyBundle → GraphSpec v1.0.
 * Implements Neo4j-style entity-relation-entity visualization with
 * filters, search, diff mode, and evidence linking.
 *
 * @module client/components/OntologyGraphExplorer
 * @version 1.0
 * @date 2025-10-10
 * @see Documentation/Development/Design Docs/Graph Spec.md Sections J, R
 */

import * as React from "react";
import { GraphSpec, GraphViewerState, FilterState, SelectionState, LayoutMode, GraphNode, GraphEdge } from "../types/graphSpec";

// Evidence item type alias - use Evidence from graphSpec directly
// The Evidence type is imported from graphSpec.ts and has all required fields
import type { Evidence } from "../types/graphSpec";
import { GraphErrorBoundary } from "./GraphErrorBoundary";
import { CytoscapeGraphCanvas } from "./CytoscapeGraphCanvas";
import "./ontology-graph-explorer.css";

interface OntologyGraphExplorerProps {
  jobId: string;
  compareJobId?: string; // For diff mode
}

/**
 * Main Ontology Graph Explorer Component
 *
 * Features:
 * - Loads GraphSpec via API hook
 * - Manages viewer state (layout, filters, selection)
 * - Renders graph visualization with elkjs or Cytoscape
 * - Provides filtering, search, and evidence panels
 * - Supports diff mode for before/after comparison
 *
 * Usage:
 * ```tsx
 * <OntologyGraphExplorer jobId="job-123" />
 * <OntologyGraphExplorer jobId="job-456" compareJobId="job-123" />
 * ```
 */
export function OntologyGraphExplorer({ jobId, compareJobId }: OntologyGraphExplorerProps): React.ReactElement {
  // State management
  const [graphSpec, setGraphSpec] = React.useState<GraphSpec | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<Error | null>(null);

  const [viewerState, setViewerState] = React.useState<GraphViewerState>({
    viewMode: "instance",  // Always show instance view (dataset-centric)
    layout: "cose",  // Default to Interactive (force-directed) layout
    filter: {
      status: "all",  // Show all edges (met + missing) by default
      pillar: undefined,
      showInferred: true,
      showRuleNodes: false,  // Hide rule nodes by default for clarity
      showPropertyNodes: false,  // Hide property nodes by default (redundant with edge labels)
    },
    selection: {
      type: null,
      id: null,
    },
    search: "",
  });

  // Fetch GraphSpec on mount or when jobId changes
  React.useEffect(() => {
    const fetchGraphSpec = async () => {
      setLoading(true);
      setError(null);

      try {
        const url = compareJobId
          ? `/analysis/${jobId}/ontology.json?compare=${compareJobId}`
          : `/analysis/${jobId}/ontology.json`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to load graph: ${response.status} ${response.statusText}`);
        }

        const data: GraphSpec = await response.json();
        setGraphSpec(data);
      } catch (err) {
        console.error("[OntologyGraphExplorer] Failed to fetch GraphSpec:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    };

    fetchGraphSpec();
  }, [jobId, compareJobId]);

  // No auto-switching to schema view - always show instance (dataset-centric) view

  // Update viewer state helpers
  const updateFilter = (partial: Partial<FilterState>) => {
    setViewerState((prev) => ({
      ...prev,
      filter: { ...prev.filter, ...partial },
    }));
  };

  const updateSelection = (partial: Partial<SelectionState>) => {
    setViewerState((prev) => ({
      ...prev,
      selection: { ...prev.selection, ...partial },
    }));
  };

  const setLayoutMode = (layout: LayoutMode) => {
    setViewerState((prev) => ({ ...prev, layout }));
  };

  const setSearch = (search: string) => {
    setViewerState((prev) => ({ ...prev, search }));
  };

  // Handle selection
  const handleSelectNode = (nodeId: string | null) => {
    updateSelection({ type: nodeId ? "node" : null, id: nodeId });
  };

  const handleSelectEdge = (edgeId: string | null) => {
    updateSelection({ type: edgeId ? "edge" : null, id: edgeId });
  };

  // SVG ref for export
  const _svgRef = React.useRef<SVGSVGElement>(null);

  // Handle SVG export
  const handleExportSVG = () => {
    const svg = document.querySelector('.graph-canvas svg') as SVGSVGElement;
    if (!svg) {
      console.error('[Export] SVG element not found');
      return;
    }

    // Clone SVG to avoid modifying the original
    const svgClone = svg.cloneNode(true) as SVGSVGElement;

    // Set explicit dimensions
    const rect = svg.getBoundingClientRect();
    svgClone.setAttribute('width', String(rect.width));
    svgClone.setAttribute('height', String(rect.height));
    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    // Serialize to string
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);

    // Create blob and download link
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `ontology-graph-${graphSpec?.dataset_id || graphSpec?.meta?.dataset_id || 'unknown'}-${Date.now()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  };

  // Render loading state
  if (loading) {
    return (
      <div className="ontology-graph-explorer loading">
        <div className="oge-loading-spinner"></div>
        <p>Loading your FAIR compliance map...</p>
      </div>
    );
  }

  // Render error state
  if (error || !graphSpec) {
    return (
      <div className="ontology-graph-explorer error">
        <div className="oge-error-icon">⚠️</div>
        <h2>Unable to Load Compliance Map</h2>
        <p>{error?.message ?? "Unknown error"}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <GraphErrorBoundary>
      <div className="ontology-graph-explorer">
        {/* Header with title and metadata */}
        <header className="graph-header">
          <div className="graph-title">
            <h1>FAIR Compliance Map{graphSpec.dataset_id && graphSpec.dataset_id !== "unknown" ? `: ${graphSpec.dataset_id}` : ""}</h1>
            {(() => {
              const d = (graphSpec as GraphSpec & { counts?: { display?: { edges?: { satisfied?: number; expected?: number } } } }).counts?.display;
              const sat = d?.edges?.satisfied ?? 0;
              const exp = d?.edges?.expected ?? 0;
              const percentage = exp > 0 ? Math.round((sat / exp) * 100) : 0;
              return (
                <div className="graph-subtitle">
                  <span className="oge-subtitle-metric">
                    {sat} of {exp} FAIR requirements met ({percentage}%)
                  </span>
                  {graphSpec.diff && <span className="oge-diff-indicator">• Diff Mode</span>}
                </div>
              );
            })()}
          </div>

          <div className="graph-controls">
            {/* View Mode Selector */}
            <div className="view-mode-selector">
              <label>Show:</label>
              <button
                className={viewerState.filter.status === "all" ? "active" : ""}
                onClick={() => updateFilter({ status: "all" })}
                data-action="view-all"
                title="Show all requirements (met and missing)"
              >
                All Requirements
              </button>
              <button
                className={viewerState.filter.status === "pass" ? "active" : ""}
                onClick={() => updateFilter({ status: "pass" })}
                data-action="view-met"
                title="Show only requirements you've met"
              >
                Met Only
              </button>
            </div>

            {/* Layout Mode Selector */}
            <div className="layout-selector">
              <label>Layout:</label>
              <button
                className={viewerState.layout === "cose" ? "active" : ""}
                onClick={() => setLayoutMode("cose")}
                data-action="layout-cose"
                title="Interactive force-directed layout"
              >
                Interactive
              </button>
              <button
                className={viewerState.layout === "elk" ? "active" : ""}
                onClick={() => setLayoutMode("elk")}
                data-action="layout-elk"
                title="Hierarchical tree layout"
              >
                Tree
              </button>
            </div>
          </div>
        </header>

        <div className="graph-content">
          {/* Left Sidebar - Filters and Legend */}
          <aside className="graph-sidebar-left">
            <section className="filter-panel">
              <h3>Filter by Pillar</h3>

              {/* Pillar Filter */}
              <div className="oge-filter-group">
                <label>Pillar:</label>
                <select
                  value={viewerState.filter.pillar ?? "all"}
                  onChange={(e) =>
                    updateFilter({ pillar: e.target.value === "all" ? undefined : (e.target.value as 'F' | 'A' | 'I' | 'R') })
                  }
                  data-action="filter-pillar"
                >
                  <option value="all">All Pillars</option>
                  <option value="F">Findable</option>
                  <option value="A">Accessible</option>
                  <option value="I">Interoperable</option>
                  <option value="R">Reusable</option>
                </select>
              </div>

            </section>

            {/* Legend */}
            <section className="legend-panel">
              <h3>What You're Seeing</h3>
              <div className="oge-legend-items">
                <div className="oge-legend-item">
                  <div className="edge-preview edge-pass"></div>
                  <span>✓ Requirement Met</span>
                </div>
                <div className="oge-legend-item">
                  <div className="edge-preview edge-expected"></div>
                  <span>○ Missing / To-Do</span>
                </div>
                <div className="oge-legend-item">
                  <div className="edge-preview edge-fail"></div>
                  <span>✗ Failed Check</span>
                </div>
                <div className="oge-legend-item">
                  <div className="node-preview node-Dataset"></div>
                  <span>Your Dataset</span>
                </div>
                <div className="oge-legend-item">
                  <div className="node-preview node-License"></div>
                  <span>License Info</span>
                </div>
                <div className="oge-legend-item">
                  <div className="node-preview node-PID"></div>
                  <span>Identifier (DOI/etc)</span>
                </div>
                {graphSpec.diff && (
                  <>
                    <div className="oge-legend-item">
                      <div className="node-preview node-added"></div>
                      <span>Added</span>
                    </div>
                    <div className="oge-legend-item">
                      <div className="node-preview node-removed"></div>
                      <span>Removed</span>
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Pillar Summary */}
            {(graphSpec.pillar_summary || graphSpec.meta?.pillar_summary) && (
              <section className="pillar-summary">
                <h3>FAIR Summary</h3>
                {Object.entries(graphSpec.pillar_summary || graphSpec.meta?.pillar_summary || {}).map(([pillar, stats]) => (
                  <div key={pillar} className="pillar-stat">
                    <div className="oge-pillar-label">{pillar}</div>
                    <div className="oge-pillar-bar">
                      <div
                        className={`oge-pillar-bar-fill pillar-${pillar} ${stats.gate ? "gate-pass" : "gate-fail"}`}
                        style={{ width: `${stats.coverage * 100}%` }}
                      ></div>
                    </div>
                    <div className="oge-pillar-score">
                      {stats.score.toFixed(1)} ({Math.round(stats.coverage * 100)}%)
                    </div>
                  </div>
                ))}
              </section>
            )}
          </aside>

          {/* Main Graph Canvas */}
          <main className="graph-canvas-container">
            {/* Search Bar */}
            <div className="graph-toolbar">
              <input
                type="text"
                className="oge-search-input"
                placeholder="Search for specific requirements or items..."
                value={viewerState.search}
                onChange={(e) => setSearch(e.target.value)}
                data-action="search"
              />
              <button className="oge-btn-export" data-action="export-svg" onClick={handleExportSVG}>
                Export Image
              </button>
            </div>

            {/* Graph Visualization Area */}
            <div className="graph-canvas">
              <CytoscapeGraphCanvas
                graphSpec={graphSpec}
                viewerState={viewerState}
                onSelectNode={handleSelectNode}
                onSelectEdge={handleSelectEdge}
              />
            </div>
          </main>

          {/* Right Sidebar - Selection Details */}
          <aside className="graph-sidebar-right">
            {viewerState.selection.type === "node" && viewerState.selection.id && (
              <NodeDetailPanel
                node={graphSpec.nodes.find((n) => n.id === viewerState.selection.id)!}
                edges={graphSpec.edges.filter(
                  (e) => e.source === viewerState.selection.id ||
                         e.target === viewerState.selection.id ||
                         e.predicate === viewerState.selection.id
                )}
                evidence={graphSpec.evidence}
              />
            )}

            {viewerState.selection.type === "edge" && viewerState.selection.id && (
              <EdgeDetailPanel
                edge={graphSpec.edges.find((e) => e.id === viewerState.selection.id)!}
                nodes={graphSpec.nodes}
                evidence={graphSpec.evidence}
              />
            )}

            {!viewerState.selection.type && (
              <div className="selection-placeholder">
                <p>Click a node or edge to view details</p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </GraphErrorBoundary>
  );
}

/**
 * Node Detail Panel
 */
function NodeDetailPanel({ node, edges, evidence: _evidence }: { node: GraphNode; edges: GraphEdge[]; evidence?: Evidence[] }): React.ReactElement {
  // For Property nodes, show usage count
  const isProperty = node.type === "Property";
  const usageCount = isProperty ? edges.length : 0;

  return (
    <div className="detail-panel node-detail">
      <h3>Node Details</h3>
      <div className="oge-detail-row">
        <label>ID:</label>
        <span className="mono">{node.id}</span>
      </div>
      <div className="oge-detail-row">
        <label>Type:</label>
        <span>{node.type}</span>
      </div>
      <div className="oge-detail-row">
        <label>Label:</label>
        <span>{node.label}</span>
      </div>
      {node.subtitle && (
        <div className="oge-detail-row">
          <label>Subtitle:</label>
          <span className="mono">{node.subtitle}</span>
        </div>
      )}
      {node.iri && (
        <div className="oge-detail-row">
          <label>IRI:</label>
          <a href={node.iri} target="_blank" rel="noopener noreferrer" className="mono">
            {node.iri}
          </a>
        </div>
      )}
      <div className="oge-detail-row">
        <label>Status:</label>
        <span className={`oge-status-badge status-${node.status || 'unknown'}`}>
          {node.type === "Property" || node.type === "Class" || node.type === "Rule"
            ? (node.status ?? "N/A")
            : (node.status ?? "unknown")}
        </span>
      </div>
      {node.pillar && (
        <div className="oge-detail-row">
          <label>Pillar:</label>
          <span className={`oge-pillar-badge pillar-${node.pillar}`}>{node.pillar}</span>
        </div>
      )}

      {/* Rule description - show prominently for FAIR requirement nodes */}
      {node.meta?.description != null && node.meta?.ruleId != null && (
        <div className="oge-detail-section rule-description">
          <h4>FAIR Requirement</h4>
          <p>{String(node.meta.description)}</p>
        </div>
      )}

      {/* OWL Definition - formal ontology axiom */}
      {node.meta?.owl != null && node.meta?.ruleId != null && (
        <div className="oge-detail-section owl-definition">
          <h4>OWL Definition</h4>
          <pre>{String(node.meta.owl)}</pre>
        </div>
      )}

      {/* SHACL Shape - validation constraint */}
      {node.meta?.shacl != null && node.meta?.ruleId != null && (
        <div className="oge-detail-section shacl-shape">
          <h4>SHACL Validation</h4>
          <pre>{String(node.meta.shacl)}</pre>
        </div>
      )}

      {/* Property-specific info */}
      {isProperty && (
        <div className="oge-detail-row">
          <label>Usage Count:</label>
          <span className="usage-count">{usageCount} edge{usageCount !== 1 ? 's' : ''}</span>
        </div>
      )}

      {node.meta && Object.keys(node.meta).length > 0 && (
        <>
          <h4>Metadata</h4>
          {Object.entries(node.meta)
            .filter(([key]) => !['description', 'owl', 'shacl'].includes(key)) // Don't duplicate fields shown above
            .map(([key, value]) => (
              <div key={key} className="oge-detail-row">
                <label>{key}:</label>
                <span>{String(value)}</span>
              </div>
            ))}
        </>
      )}

      {node.properties && Object.keys(node.properties).length > 0 && (
        <>
          <h4>Properties</h4>
          {Object.entries(node.properties).map(([key, value]) => (
            <div key={key} className="oge-detail-row">
              <label>{key}:</label>
              <span>{String(value)}</span>
            </div>
          ))}
        </>
      )}

      <h4>{isProperty ? "Used In" : "Connected Edges"} ({edges.length})</h4>
      <div className="edge-list">
        {edges.slice(0, 10).map((e) => (
          <div key={e.id} className="edge-item">
            <span className={`oge-status-badge status-${e.status}`}>{e.status?.[0] ?? "?"}</span>
            <span className="oge-edge-label">{e.label || e.predicate}</span>
          </div>
        ))}
        {edges.length > 10 && <div className="more-indicator">...and {edges.length - 10} more</div>}
      </div>
    </div>
  );
}

/**
 * Edge Detail Panel
 */
function EdgeDetailPanel({ edge, nodes, evidence }: { edge: GraphEdge; nodes: GraphNode[]; evidence?: Evidence[] }): React.ReactElement {
  const sourceNode = nodes.find((n) => n.id === edge.source);
  const targetNode = nodes.find((n) => n.id === edge.target);

  return (
    <div className="detail-panel edge-detail">
      <h3>Edge Details</h3>
      <div className="oge-detail-row">
        <label>ID:</label>
        <span className="mono">{edge.id}</span>
      </div>
      <div className="oge-detail-row">
        <label>Predicate:</label>
        <span className="mono">{edge.predicate ?? "unknown"}</span>
      </div>
      <div className="oge-detail-row">
        <label>Label:</label>
        <span>{edge.label}</span>
      </div>
      <div className="oge-detail-row">
        <label>Status:</label>
        <span className={`oge-status-badge status-${edge.status}`}>{edge.status ?? "unknown"}</span>
      </div>
      {edge.pillar && (
        <div className="oge-detail-row">
          <label>Pillar:</label>
          <span className={`oge-pillar-badge pillar-${edge.pillar}`}>{edge.pillar}</span>
        </div>
      )}
      {edge.inferred && (
        <div className="oge-detail-row">
          <label>Inferred:</label>
          <span className="oge-badge">Yes</span>
        </div>
      )}

      <h4>Connection</h4>
      <div className="connection-flow">
        <div className="connection-node">
          <div className="node-type">{sourceNode?.type}</div>
          <div className="oge-node-label">{sourceNode?.label}</div>
        </div>
        <div className="connection-arrow">→</div>
        <div className="connection-node">
          <div className="node-type">{targetNode?.type}</div>
          <div className="oge-node-label">{targetNode?.label}</div>
        </div>
      </div>

      {edge.evidenceIds && edge.evidenceIds.length > 0 && (
        <>
          <h4>Evidence ({edge.evidenceIds.length})</h4>
          <div className="oge-evidence-list">
            {edge.evidenceIds.slice(0, 5).map((evId: string) => {
              const ev = evidence?.find((e) => e.id === evId);
              return (
                <div key={evId} className="evidence-item">
                  <div className="evidence-type">{ev?.type ?? "unknown"}</div>
                  <div className="evidence-snippet">{ev?.snippet ? String(ev.snippet) : "No snippet available"}</div>
                </div>
              );
            })}
            {edge.evidenceIds.length > 5 && (
              <div className="more-indicator">...and {edge.evidenceIds.length - 5} more</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
